import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../../utils/firebase/firebase.utils';
import { selectUser } from '../../../../store/user/user.selector';

// styles
import {
    CollectionUpdatedEntryContainer,
    TopContent,
    LeftContent,
    DateDisplay,
    ActionDisplayContainer,
    ActionDisplay,
    SaleTitle,
    CardNumberDisplay,
    CardCount,
    CardsDisplay
} from './CollectionUpdatedEntry.styles';

// assets
import cardIcon from '../../../../assets/system/icons/card-icon.png';

// components
import CardDisplay from './components/card-display/CardDisplay';
import { SALE_ACTIVITY_TYPES } from '../../../../utils/sales/sales.constants';

const isSaleSold = (actionType) => actionType === SALE_ACTIVITY_TYPES.SOLD;
const isSaleReturned = (actionType) => actionType === SALE_ACTIVITY_TYPES.RETURNED;
const isRemovalAction = (actionType) => (
  actionType === 'removedFromCollection' || isSaleSold(actionType)
);

export default function CollectionUpdatedEntry({ content }) {
    const user = useSelector(selectUser);
    const prevCount = Number(content.details?.prevCount) || 0;
    const increment = Number(content.details?.increment) || 0;
    const newCount = prevCount + increment;
    const actionType = content.actionType;
    const addedCards = content.details?.cards;
    const addedCardCount = typeof content.details?.cards?.totalCardCount === 'number'
      ? content.details.cards.totalCardCount
      : increment;
    const formattedTimestamp = content.timestamp?.toDate().toLocaleString();
    const [isExpanded, setIsExpanded] = useState(false);
    const [removedCards, setRemovedCards] = useState(false);
    const [customCategoryNames, setCustomCategoryNames] = useState({});
    const saleTitle = content.details?.title;
    const saleSku = content.details?.ebaySku;
    const groupCode = content.details?.groupCode;
    const skippedCount = Number(content.details?.skippedCount) || 0;
    const codeLabel = saleSku || groupCode;
    
    // Calculate the card count for display (use absolute value of increment for removed cards)
    const displayCardCount = isRemovalAction(actionType)
        ? Math.abs(increment)
        : addedCardCount;

    const actionLabel = isSaleSold(actionType)
        ? `Sold ${displayCardCount} ${displayCardCount === 1 ? 'card' : 'cards'}`
        : isSaleReturned(actionType)
            ? `Returned ${displayCardCount} ${displayCardCount === 1 ? 'card' : 'cards'}`
            : `${actionType === 'addedToCollection' ? '+' : '-'}${displayCardCount} ${displayCardCount === 1 ? 'card' : 'cards'}`;

    const handleExpand = () => {
        if (isExpanded) {
            setIsExpanded(false);
        } else {
            setIsExpanded(true);
        }
    }

    useEffect(() => {
        setRemovedCards(isRemovalAction(actionType));
    }, [actionType])

    // Fetch custom category names
    useEffect(() => {
        const fetchCustomCategoryNames = async () => {
            if (!user?.uid || !addedCards) return;

            const seenCategories = new Set();
            const fetchPromises = [];

            // Iterate through the nested structure to find all custom categories
            for (const sportKey of Object.keys(addedCards)) {
                if (sportKey === "totalCardCount") continue;
                const sportData = addedCards[sportKey];

                for (const yearKey of Object.keys(sportData)) {
                    if (yearKey === "totalCardCount") continue;
                    const yearData = sportData[yearKey];

                    for (const manufacturerKey of Object.keys(yearData)) {
                        if (manufacturerKey === "totalCardCount") continue;
                        const manufacturerData = yearData[manufacturerKey];

                        for (const seriesKey of Object.keys(manufacturerData)) {
                            if (seriesKey === "totalCardCount") continue;
                            const seriesData = manufacturerData[seriesKey];

                            for (const cardTypeKey of Object.keys(seriesData)) {
                                if (cardTypeKey === "totalCardCount") continue;
                                const cardTypeData = seriesData[cardTypeKey];

                                for (const categoryKey of Object.keys(cardTypeData)) {
                                    if (categoryKey === "totalCardCount") continue;
                                    
                                    // Check if this is a custom category
                                    if (categoryKey.startsWith('custom:') && !seenCategories.has(categoryKey)) {
                                        seenCategories.add(categoryKey);
                                        const customCategoryId = categoryKey.replace('custom:', '');
                                        const categoryPath = `users/${user.uid}/collection/${sportKey}/${yearKey}/${manufacturerKey}/${seriesKey}/${cardTypeKey}/customCategories/${customCategoryId}`;
                                        
                                        fetchPromises.push(
                                            getDoc(doc(db, categoryPath))
                                                .then(docSnap => {
                                                    if (docSnap.exists()) {
                                                        return { key: categoryKey, name: docSnap.data().name };
                                                    } else {
                                                        return { key: categoryKey, name: categoryKey }; // Fallback to ID if not found
                                                    }
                                                })
                                                .catch(() => {
                                                    return { key: categoryKey, name: categoryKey }; // Fallback to ID on error
                                                })
                                        );
                                    }
                                }
                            }
                        }
                    }
                }
            }

            // Wait for all fetches to complete and build the map
            const results = await Promise.all(fetchPromises);
            const categoryMap = {};
            results.forEach(({ key, name }) => {
                categoryMap[key] = name;
            });
            setCustomCategoryNames(categoryMap);
        };

        if (isExpanded && user?.uid) {
            fetchCustomCategoryNames();
        }
    }, [isExpanded, addedCards, user?.uid])

    // Helper function to get display name for category
    const getCategoryDisplayName = (categoryKey) => {
        if (categoryKey.startsWith('custom:')) {
            return customCategoryNames[categoryKey] || categoryKey;
        }
        return categoryKey;
    }

    return (
        <CollectionUpdatedEntryContainer $isExpanded={isExpanded} className='widget-bg'>
            <TopContent>
                <LeftContent>
                    <DateDisplay>{formattedTimestamp}</DateDisplay>
                    <ActionDisplayContainer>
                        <ActionDisplay>
                            {actionLabel}
                            {(saleTitle || codeLabel) && (
                                <SaleTitle>
                                    {saleTitle || 'Code'}
                                    {codeLabel ? ` · ${codeLabel}` : ''}
                                </SaleTitle>
                            )}
                            {skippedCount > 0 && (
                                <SaleTitle>
                                    {skippedCount} not added — unlock the checklist to add them
                                </SaleTitle>
                            )}
                        </ActionDisplay>
                        <h3 onClick={handleExpand}>VIEW {isExpanded ? '↑' : '↓'}</h3>
                    </ActionDisplayContainer>

                </LeftContent>
                <CardNumberDisplay>
                    <CardCount style={{ opacity: 0.5 }}>
                        <h4>{prevCount}</h4>
                    </CardCount>
                    <span>&#x279F;</span>
                    <CardCount>
                        <h4>{newCount}</h4>
                        <img src={cardIcon} alt="card icon" />
                    </CardCount>
                </CardNumberDisplay>
            </TopContent>
            {isExpanded && addedCards &&
                <CardsDisplay>
                    {Object.keys(addedCards).map((sportKey) => {
                        if (sportKey === "totalCardCount") return null;
                        const sportData = addedCards[sportKey];
                        return (
                            <div key={sportKey} className="sport-section">
                                <h2>{sportKey}</h2>
                                {Object.keys(sportData).map((yearKey) => {
                                    if (yearKey === "totalCardCount") return null;
                                    const yearData = sportData[yearKey];
                                    return (
                                        <div key={yearKey} className="year-section">
                                            <h2>{yearKey}</h2>
                                            {Object.keys(yearData).map((manufacturerKey) => {
                                                if (manufacturerKey === "totalCardCount") return null;
                                                const manufacturerData = yearData[manufacturerKey];
                                                return (
                                                    <div key={manufacturerKey} className="manufacturer-section">
                                                        <h2>{manufacturerKey}</h2>
                                                        {Object.keys(manufacturerData).map((seriesKey) => {
                                                            if (seriesKey === "totalCardCount") return null;
                                                            const seriesData = manufacturerData[seriesKey];
                                                            return (
                                                                <div key={seriesKey} className="series-section">
                                                                    <h2>{seriesKey}</h2>
                                                                    {Object.keys(seriesData).map((cardTypeKey) => {
                                                                        if (cardTypeKey === "totalCardCount") return null;
                                                                        const cardTypeData = seriesData[cardTypeKey];
                                                                        return (
                                                                            <div key={cardTypeKey} className="cardtype-section">
                                                                                <h2>{cardTypeKey}</h2>
                                                                                {Object.keys(cardTypeData).map((categoryKey) => {
                                                                                    if (categoryKey === "totalCardCount") return null;
                                                                                    const categoryData = cardTypeData[categoryKey];
                                                                                    const categoryDisplayName = getCategoryDisplayName(categoryKey);
                                                                                    return (
                                                                                        <div key={categoryKey} className="cardtype-category-section">
                                                                                            <h2>{categoryDisplayName}</h2>
                                                                                            {Object.keys(categoryData)
                                                                                                .filter(cardNumberKey => cardNumberKey !== "totalCardCount") // Exclude the "totalCardCount" key
                                                                                                .sort((a, b) => {
                                                                                                    const getNumericPart = (cardNumber) => {
                                                                                                        const parts = cardNumber.split('-'); // Split on '-'
                                                                                                        const lastPart = parts[parts.length - 1]; // Get the last part
                                                                                                        return parseInt(lastPart.replace(/^\D+/g, ''), 10) || 0; // Remove non-digits and parse to integer
                                                                                                    };
                                                                                                    return getNumericPart(a) - getNumericPart(b); // Sort based on numeric value
                                                                                                })
                                                                                                .map((cardNumberKey) => {
                                                                                                    const card = categoryData[cardNumberKey];
                                                                                                    return (
                                                                                                        <div key={card.cardNumber} style={{ opacity: removedCards ? "0.67" : "1.0" }}>
                                                                                                            <CardDisplay
                                                                                                                key={`${card.cardNumber}${categoryKey}`}
                                                                                                                wasRemoved={removedCards}
                                                                                                                card={card}
                                                                                                                path={`/checklists/${sportKey}/${yearKey}/${manufacturerKey}/${seriesKey}/${cardTypeKey}/${categoryKey}`}
                                                                                                            />
                                                                                                        </div>
                                                                                                    );
                                                                                                })}
                                                                                        </div>
                                                                                    );
                                                                                })}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                })}
                            </div>
                        );

                    })}
                </CardsDisplay>
            }
        </CollectionUpdatedEntryContainer>
    )
}
